/**
 * Streaming Risk Assessment client.
 *
 * Opens an SSE connection to POST /api/scan/risk-stream and yields one event
 * per logical piece of the model's reply (overallRiskSummary, each stockRisk,
 * each conflict, each missedRisk, then a terminal "done" event). The caller
 * iterates with `for await` and can stop the stream via AbortController to
 * save Anthropic tokens when the user navigates away.
 *
 * Why a custom fetch + ReadableStream instead of native EventSource:
 *   - EventSource is GET-only; we need POST with a JSON body
 *   - EventSource has no way to pass an Authorization header
 *   - Fetch+ReadableStream gives us first-class AbortSignal support
 *
 * Browser support: Chrome 63+ / Firefox 57+ / Safari 11+ (async generators
 * + fetch ReadableStream + AbortSignal).
 */

import { getToken } from './apiService';

const AI_SERVICE_URL = process.env.REACT_APP_AI_SERVICE_URL || 'http://localhost:8080';

/**
 * Yield events of shape `{ type, data }` where:
 *   - type ∈ { "overallRiskSummary", "stockRisk", "conflict", "missedRisk",
 *              "done", "error" }
 *   - data is the parsed JSON from the SSE `data:` line
 *
 * @param {object} payload - the RiskAssessmentRequest body
 * @param {AbortSignal} [signal] - aborts both fetch and stream read
 */
export async function* streamRiskAssessment(payload, signal) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${AI_SERVICE_URL}/api/scan/risk-stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Risk stream request failed: ${response.status} ${text}`);
  }
  if (!response.body) {
    throw new Error('Risk stream response has no body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE event blocks are separated by a blank line ("\n\n"). Each block
      // may carry an `event:` line and one or more `data:` lines.
      let sep;
      while ((sep = buffer.indexOf('\n\n')) >= 0) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const evt = parseSseBlock(block);
        if (evt) yield evt;
      }
    }
    // Flush any trailing block (server should send "\n\n" but be defensive)
    if (buffer.trim().length > 0) {
      const evt = parseSseBlock(buffer);
      if (evt) yield evt;
    }
  } finally {
    try { reader.releaseLock(); } catch (_) { /* ignore */ }
  }
}

/**
 * Parse one SSE event block into `{ type, data }`. Returns null if the block
 * has no `data:` line or the JSON parse fails (e.g., heartbeat lines).
 */
function parseSseBlock(block) {
  let eventName = 'message';
  let dataLine = null;
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLine = (dataLine == null ? '' : dataLine + '\n') + line.slice(5).trim();
    }
    // SSE also allows ":comment" and "id:"/"retry:" — we ignore those.
  }
  if (dataLine == null) return null;
  try {
    return { type: eventName, data: JSON.parse(dataLine) };
  } catch (_) {
    return { type: eventName, data: dataLine };
  }
}
